import type { State } from "../State";
import type Button from "./Button";
import type Actor from "./Actor";
import type ButtonChannel from "./ButtonChannel";
import type Channel from "./Channel";
import type ConnectableObject from "./ConnectableObject";
import Connection, { ConnectionType, getTypeFromDevice } from "./Connection";
import { ChannelType, channelTypeInfo } from "./ChannelType";
import AllowedConnections from "./AllowedConnections";
import type Multiplexer from "./Multiplexer";
import type Scene from "./Scene";
import { itx } from "../translationItx/itx";
import { findUnusedButtonChannel } from "./findUnusedButtonChannel";

/**
 * Fachliche Erstellung echter Geräteverbindungen, gemeinsam für Zeichenfläche und Dashboard.
 *
 * 1. assessConnection erstellt ausschließlich einen Vorschlag: kein Anlegen von Kanälen,
 *    keine Typänderung und kein Undo-Eintrag während des Hoverns.
 * 2. createModelConnection übernimmt beim Loslassen die Änderungen über die Modellfabrik.
 *    Die Fabrik prüft ihre Regeln nochmals; eine Vorschau ist keine Schreibgarantie.
 * 3. persistConnectionEndpoint übernimmt dabei eventuell vorhandene temporäre Endpunkte.
 *
 * Pointerzustand, Auswahl, Darstellung und der Abschluss der Undo-Interaktion bleiben im
 * aufrufenden UI. Sicherungszuordnungen stehen separat in fuseConnectionCreation.ts:
 * Sie ändern consumer.fuse und erzeugen daraus virtuelle Linien, keine Connection-Objekte.
 */

/** Konkrete Endpunkte und Typ für die Vorschau; die ursprünglichen Geräte können auf Kanäle aufgelöst sein. */
export type ConnectionProposal = {
  kind?: "connection";
  source: ConnectableObject;
  target: ConnectableObject;
  type: ConnectionType;
  allowed: boolean;
  reason?: string;
  /** Original type of an automatically selected key, used to revalidate the preview on commit. */
  buttonChannelType?: ChannelType;
};

/**
 * Ein ganzer Taster übernimmt für einen Verbraucher eine freie physische Taste.
 * Die Auswahl folgt den Regeln von Button.findConnectableChannel.
 * Ohne freie Taste wird abgelehnt, statt hier einen zusätzlichen Kanal anzulegen.
 */
function automaticButtonConnection(source: ConnectableObject, target: ConnectableObject): ConnectionProposal | undefined {
  const button = source.className === "Button" && target.isConsumer ? source as Button
    : target.className === "Button" && source.isConsumer ? target as Button : undefined;
  if (!button) return undefined;
  const consumer = button === source ? target : source;
  const channel = findUnusedButtonChannel(button, consumer);
  if (channel && !AllowedConnections.isConnectionAllowed(source, target, true)) {
    return {source, target, type: ConnectionType.Button, allowed: false,
      reason: "Dieser Verbraucher kann nicht mit diesem Taster verbunden werden."};
  }
  if (channel) {
    return {
      source: button === source ? channel : consumer,
      target: button === target ? channel : consumer,
      type: ConnectionType.Button, allowed: true, buttonChannelType: channel.channelType,
    };
  }
  return {source, target, type: ConnectionType.Button, allowed: false, reason: "Dieser Taster hat keine freie passende Taste."};
}

/**
 * Liefert die vorhandenen Kanäle eines ganzen Aktors/Tasters für die Vorschau.
 * Die klassische findConnectableChannel-Methode darf hier nicht verwendet werden,
 * da sie neue Kanäle erzeugen und vorhandene Typen verändern kann.
 */
function concreteEndpoints(object: ConnectableObject): ConnectableObject[] {
  if (object.className === "Actor") {
    const channels: ConnectableObject[] = [];
    for (const channel of (object as Actor).channels) {
      if (!channel.isDeleted && channel.isActorChannel && !channel.isStatusChannel) channels.push(channel);
    }
    return channels;
  }
  if (object.className !== "Button") return [object];
  const button = object as Button;
  const result: ConnectableObject[] = [];
  for (const channel of button.buttonChannels) {
    if (!channel.isDeleted && !channel.isStatusChannel) result.push(channel);
  }
  return result;
}

/**
 * Bestimmt den fachlichen Typ unabhängig von der Ziehrichtung.
 * Verbraucher untereinander bedeuten TerminalVia, außer beim Kontakt-Rollo-Paar.
 * Die Reihenfolge der folgenden Spezialfälle ist relevant, etwa für Szene plus Taster.
 */
export function proposedConnectionType(source: ConnectableObject, target: ConnectableObject): ConnectionType {
  if (source.isConsumer && target.isConsumer) {
    const contactAndBlind = (source.channelType === ChannelType.Lock && channelTypeInfo(target.channelType).isRollo)
      || (target.channelType === ChannelType.Lock && channelTypeInfo(source.channelType).isRollo);
    return contactAndBlind ? ConnectionType.Button : ConnectionType.TerminalVia;
  }
  if (source.isScene || target.isScene) return ConnectionType.Scene;
  if (source.isMultiplexer || target.isMultiplexer) return ConnectionType.Multiplexer;
  if (source.isActorChannel || target.isActorChannel) return ConnectionType.Channel;
  if (source.isButton || target.isButton || source.isButtonChannel || target.isButtonChannel) return ConnectionType.Button;
  return getTypeFromDevice(source);
}

/**
 * Prüft Identität/Löschung und sucht das erste zulässige konkrete Endpunktpaar.
 * Der Vorschlag enthält die aufgelösten Kanäle, damit beim Abschluss nicht stillschweigend
 * ein anderer Kanal gewählt wird. Ablehnungen können unmittelbar im UI angezeigt werden.
 */
export function assessConnection(source: ConnectableObject, target: ConnectableObject): ConnectionProposal {
  const type = proposedConnectionType(source, target);
  const rejected = { source, target, type, allowed: false, reason: "Diese Anschlüsse können nicht verbunden werden." };
  if (source.id === target.id || source.isDeleted || target.isDeleted) return rejected;
  const automatic = automaticButtonConnection(source, target);
  if (automatic) return automatic;
  for (const from of concreteEndpoints(source)) {
    for (const to of concreteEndpoints(target)) {
      if (from.id === to.id || from.isDeleted || to.isDeleted) continue;
      if (AllowedConnections.isConnectionAllowed(from, to, true)) {
        return { source: from, target: to, type: proposedConnectionType(from, to), allowed: true };
      }
    }
  }
  return rejected;
}

/**
 * Schreibt eine echte Verbindung über Connection.createConnection und führt die gemeinsamen
 * Abschlussaktionen aus. requestedType bewahrt insbesondere die Typwahl des klassischen
 * Zeichenentwurfs; Unknown/FromSourceDevice werden anhand der konkreten Endpunkte aufgelöst.
 *
 * buttonChannelType bewahrt den ursprünglichen Typ der automatisch ausgewählten Taste.
 * Bei Ablehnung wird deren Typ zurückgesetzt; die Funktion ist keine allgemeine Modelltransaktion.
 * Der Aufrufer beendet die Undo-Interaktion, auch wenn die Fabrik eine Verbindung ablehnt.
 */
export function createModelConnection(
  source: ConnectableObject,
  target: ConnectableObject,
  context: Pick<State, "sceneGroup">,
  requestedType?: ConnectionType,
  buttonChannelType?: ChannelType,
): { connection?: Connection; msg?: string } {
  // Preserve the legacy redirection from an actor channel to its connected consumer.
  if (source.className.includes("Button") && target.isActorChannel) {
    const consumer = (target as Channel).getConnectedConsumer();
    if (consumer) {
      target = consumer;
      requestedType = ConnectionType.Button;
    }
  }
  const automatic = automaticButtonConnection(source, target);
  if (automatic) {
    if (!automatic.allowed) return {msg: automatic.reason};
    source = automatic.source;
    target = automatic.target;
    requestedType = automatic.type;
    buttonChannelType = automatic.buttonChannelType;
  }
  if (target.className === "Button") {
    const button = target as Button;
    target = button.findConnectableChannel(source.isScene ? ConnectionType.Scene : ConnectionType.Button, source)
      ?? button.createNewRequestChannel(undefined, button.isDecentralActor ? "Actor" : "Button");
  }
  if (source.className === "Button") {
    const button = source as Button;
    source = button.findConnectableChannel(target.isScene ? ConnectionType.Scene : ConnectionType.Button, target)
      ?? button.createNewRequestChannel(undefined, button.isDecentralActor ? "Actor" : "Button");
  }
  let type = requestedType;
  if (!type || type === ConnectionType.Unknown || type === ConnectionType.FromSourceDevice) {
    type = proposedConnectionType(source, target);
  }
  let assignedChannel: ConnectableObject | undefined;
  if (buttonChannelType !== undefined) {
    if (source.isButtonChannel) assignedChannel = source;
    else if (target.isButtonChannel) assignedChannel = target;
  }
  // Eine beim Hover freie Taste kann inzwischen belegt oder gelöscht worden sein.
  const assignmentPartner = assignedChannel === source ? target : source;
  if (assignedChannel && (assignedChannel.isDeleted || assignedChannel.connections.size !== 0
    || assignedChannel.channelType !== buttonChannelType
    || !(assignedChannel as Channel).isConnectable(type, assignmentPartner))) {
    return {msg: "Diese Taste ist nicht mehr frei oder passend."};
  }
  const previousChannelType = assignedChannel?.channelType;
  let created = false;
  let result: ReturnType<typeof Connection.createConnection>;
  try {
    // Channel adaptation belongs to Connection.createConnection / ButtonChannel.autoSetChannelType.
    result = Connection.createConnection(source, target, type);
    created = !!result.connection;
  } finally {
    if (!created && assignedChannel && previousChannelType !== undefined) assignedChannel.channelType = previousChannelType;
  }
  const connection = result.connection;
  if (!connection) return result;
  // Erst nach erfolgreicher Erstellung temporäre Endpunkte in ihre echten Sammlungen übernehmen.
  persistConnectionEndpoint(source, context);
  persistConnectionEndpoint(target, context);
  connection.isNew = true;
  connection.isHighlighted = source.selected || target.selected;
  if (connection.type === ConnectionType.Channel) {
    // Bewahrt die bisherige Nachbereinigung sämtlicher alter Kanalzuordnungen.
    // Die Fabrik ersetzt selbst ebenfalls eine alte Zuordnung; die spätere Homogenisierung
    // muss diese unterschiedlichen Umfänge berücksichtigen, statt einen Schritt blind zu löschen.
    const consumer = source.isConsumer ? source : target.isConsumer ? target : undefined;
    for (const previous of consumer?.connections.asArray() ?? []) {
      if (previous !== connection && previous.type === ConnectionType.Channel) previous.delete();
    }
  }
  // Geräteseitige Abschluss-Hooks und UI-Meldungen gehören zum erfolgreichen Commit.
  source.onConnect(connection);
  target.onConnect(connection);
  source.raiseOnChange("childCollection");
  target.raiseOnChange("childCollection");
  return result;
}

/**
 * Übernimmt die vom klassischen Zeichenweg vorbereiteten Endpunkte nach erfolgreichem Commit.
 * @NEW/@NEWC kennzeichnen noch nicht einsortierte Tasterkanäle. Szenen und Multiplexer
 * werden nur einmal in die Projektsammlung aufgenommen. Bestehende Objekte bleiben unberührt.
 */
export function persistConnectionEndpoint(obj: ConnectableObject | undefined, context: Pick<State, "sceneGroup">) {
  if (!obj) return;
  if (obj.tag === "@NEW" && obj.isButtonChannel) {
    const button = obj.parent as Button;
    if (!button) return;
    button.buttonChannels.add(obj as ButtonChannel);
    obj.tag = button.findFreeButtonTag().tag;
    obj.name = "";
  } else if (obj.tag === "@NEWC" && obj.isChannel) {
    const button = obj.parent as Button;
    if (!button) return;
    button.channels.add(obj as Channel);
    obj.tag = button.findFreeActorButtonTag().tag;
    obj.name = "";
  } else if (obj.isScene && !(obj as Scene).sceneNumber) {
    persistNewConnectionScene(obj as Scene, context);
  } else if (obj.isMultiplexer && !(obj as Multiplexer).sceneNumber) {
    persistNewConnectionMultiplexer(obj as Multiplexer, context);
  }
}

/** Vergibt die bisherige nächste Szenennummer, ohne ein bereits gespeichertes Objekt umzubenennen. */
function persistNewConnectionScene(scene: Scene, context: Pick<State, "sceneGroup">): void {
  const existing = context.sceneGroup.sortBy("tag");
  if (existing.some(item => item.id === scene.id)) return;
  let nextNumber = 0;
  for (const item of existing) {
    if (!item.isMultiplexer && item.sceneNumber >= nextNumber) nextNumber = item.sceneNumber + 1;
  }
  scene.sceneNumber = nextNumber;
  scene.name = itx("components.Scene", "Scene") + " " + nextNumber;
  context.sceneGroup.add(scene);
}

/** Übernimmt den bisherigen Multiplexer-Abschluss; dessen Nummerierung wird hier nicht neu definiert. */
function persistNewConnectionMultiplexer(multiplexer: Multiplexer, context: Pick<State, "sceneGroup">): void {
  const existing = context.sceneGroup.sortBy("tag");
  if (existing.some(item => item.id === multiplexer.id)) return;
  let nextNumber = 1;
  const tag = `M${nextNumber}`;
  for (const item of existing) {
    if (item.isMultiplexer && item.tag === tag) nextNumber++;
  }
  multiplexer.tag = tag;
  multiplexer.name = itx("models.Multiplexer", "Multiplexer") + " " + nextNumber;
  context.sceneGroup.add(multiplexer);
}

